/* eslint-disable @typescript-eslint/no-unused-vars, @typescript-eslint/no-non-null-assertion, react/no-deprecated */
import { Spinner } from '@cian/ui-kit';
import * as React from 'react';
import { throttle } from 'throttle-debounce';

import { SubscriptionContainer } from './Subscriptions/Subscription';
import * as styles from './styles/subscriptionsSection.css';
import { IGetSubscriptionsResponseSchema } from '../../../repositories/subscriptions/entities/schemas/GetSubscriptionsResponseSchema';
import { IFailedAction } from '../../../store/failedAction';
import { ESubscriptionsStatus } from '../../../store/subscriptions';
import * as S from '../../base/Segment';

export interface ISubscriptionsListProps {
  failedAction: IFailedAction | null;
  subscriptions: IGetSubscriptionsResponseSchema | null;
  status: ESubscriptionsStatus;
  isMobileLayout?: boolean;

  getSubscriptionList(categoryName: string, offset: number): void;
}

export type TSubscriptionsListProps = ISubscriptionsListProps;

export interface ISubscriptionsListState {
  hasMore: boolean;
}

const SCROLL_BOTTOM_OFFSET = 300;

function formattedFrequenciesOptions(frequencies: { name: string; value: string }[]) {
  return frequencies.map(frequency => {
    return {
      label: frequency.name,
      value: frequency.value,
    };
  });
}

export class SubscriptionsList extends React.Component<TSubscriptionsListProps, ISubscriptionsListState> {
  private subscriptionsListContainerElement: HTMLDivElement | null = null;
  private lastScrollTop = 0;

  private readonly handleScroll = (event: UIEvent) => {
    const { status, subscriptions } = this.props;
    const { hasMore } = this.state;

    if (
      !this.subscriptionsListContainerElement ||
      !subscriptions ||
      !hasMore ||
      status === ESubscriptionsStatus.Loading ||
      status === ESubscriptionsStatus.NextPageLoading
    ) {
      return;
    }

    const scrollTop = this.subscriptionsListContainerElement.scrollTop;
    const scrollHeight = this.subscriptionsListContainerElement.scrollHeight;
    const offsetHeight = this.subscriptionsListContainerElement.offsetHeight;
    const contentHeight = scrollHeight - offsetHeight;

    if (this.lastScrollTop < scrollTop && contentHeight <= scrollTop + SCROLL_BOTTOM_OFFSET) {
      this.props.getSubscriptionList(subscriptions.categoryName, (subscriptions.subscriptions || []).length);
    }

    this.lastScrollTop = scrollTop;
  };

  public constructor(props: TSubscriptionsListProps) {
    super(props);

    this.handleScroll = throttle(300, this.handleScroll);
    this.addedLoading = throttle(300, this.addedLoading);
    this.state = {
      hasMore: props.subscriptions
        ? (props.subscriptions.total || 0) > (props.subscriptions.subscriptions || []).length
        : false,
    };
  }

  public componentDidMount() {
    if (this.subscriptionsListContainerElement) {
      this.subscriptionsListContainerElement.addEventListener('scroll', this.handleScroll, { capture: false });
    }
  }

  public UNSAFE_componentWillReceiveProps(nextProps: TSubscriptionsListProps) {
    if (nextProps.subscriptions !== this.props.subscriptions) {
      const { subscriptions } = nextProps;

      if (subscriptions) {
        this.setState(
          {
            /**
             * Делаем total - 1, так как на момент ответа апи, бекенд еще не успевает
             * пересчитать это поле, после удаления из списка
             */
            hasMore: (subscriptions.total || 0) - 1 > (subscriptions.subscriptions || []).length,
          },
          () => {
            this.addedLoading(subscriptions);
          },
        );
      }

      if (this.props.status === ESubscriptionsStatus.Loading && this.props.status !== nextProps.status) {
        if (this.subscriptionsListContainerElement) {
          this.subscriptionsListContainerElement.scrollTop = 0;
        }
      }
    }
  }

  public componentWillUnmount() {
    if (this.subscriptionsListContainerElement) {
      this.subscriptionsListContainerElement.removeEventListener('scroll', this.handleScroll, { capture: false });
    }
  }

  public render() {
    if (!this.props.subscriptions) {
      return null;
    }

    const {
      status,
      subscriptions: { type, categoryName, frequenciesList, subscriptions },
      isMobileLayout,
    } = this.props;

    return (
      <div
        ref={(ref: HTMLDivElement | null) => (this.subscriptionsListContainerElement = ref)}
        className={styles['subscriptions_list']}
      >
        <S.SegmentGroup>
          {subscriptions?.map(({ isActive, id, subtitle, title: subscriptionTitle, frequency, url }) => (
            <S.Segment key={`${categoryName}_${id}_${url}`}>
              <SubscriptionContainer
                itemId={id}
                categoryName={categoryName}
                type={type}
                title={subscriptionTitle}
                isActive={Boolean(isActive)}
                subtitle={subtitle || ''}
                frequency={frequency}
                frequenciesList={
                  frequenciesList
                    ? formattedFrequenciesOptions(frequenciesList as unknown as { name: string; value: string }[])
                    : null
                }
                url={url}
              />
            </S.Segment>
          ))}
        </S.SegmentGroup>

        {this.state.hasMore &&
          status !== ESubscriptionsStatus.Loading &&
          status === ESubscriptionsStatus.NextPageLoading && (
            <div className={styles['preloader_more']}>
              <span>
                <Spinner size={32} id={`subscriptions_section_preloader${isMobileLayout ? '_mobile' : ''}`} />
              </span>
            </div>
          )}
      </div>
    );
  }

  private addedLoading = (subscriptions: IGetSubscriptionsResponseSchema | null) => {
    const { status } = this.props;

    if (this.subscriptionsListContainerElement && subscriptions) {
      const scrollHeight = this.subscriptionsListContainerElement.scrollHeight;
      const offsetHeight = this.subscriptionsListContainerElement.offsetHeight;
      const isNoPossibilityScroll = scrollHeight > 0 && offsetHeight > 0 && scrollHeight === offsetHeight;

      /**
       * Если не добавлять 1 для offset - при быстром удалении из списка, может возникнуть
       * лаг, когда последний элемент из старого запроса и первый элемент из нового совпадают и
       * перестает работать удаление. Но если offset = 1, его нужно сбросить в 0, иначе не покажется крайний
       * элемент подписки.
       */
      const subscriptionLenght = (this.props.subscriptions!.subscriptions || []).length + 1;
      const offset = subscriptionLenght === 1 ? 0 : subscriptionLenght;

      /**
       * Если высота скролящейся части блока равна всей высоте блока, при этом
       * еще есть не показанные сохраненные запросы, делаем запрос за ними.
       */
      if (isNoPossibilityScroll && this.state.hasMore && status !== ESubscriptionsStatus.NextPageLoading) {
        this.props.getSubscriptionList(subscriptions.categoryName, offset);
      }
    }
  };
}
